import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { UserRole } from '../../types/auth';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';

/**
 * User Management Tab
 * Implements Requirement 82 AC 1: User management with RBAC roles
 * Supports roles: CFO, FinOps_Analyst, Engineering, Finance, Tenant_Admin
 */
export const UserManagementTab: React.FC = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Holds the one-time password from a reset OR an invite so it can be shown
  // in a modal the admin can actually select and copy from. `kind` drives the
  // wording: an invite must not claim it "cleared sign-in blocks" on an
  // account that was created seconds ago.
  const [resetResult, setResetResult] = useState<
    { userName: string; password: string; kind: 'reset' | 'invite'; emailSent?: boolean } | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  // Guards against a double-click firing two concurrent invites. The second
  // request loses the race against Keycloak's uniqueness check and returns
  // 409 "User with this email already exists" - and because a 409 rejects
  // instantly while the winning request is still assigning roles and setting
  // the password, the ERROR alert appeared BEFORE the success one. Confirmed
  // live: an admin inviting a genuinely new address saw "already exists",
  // clicked OK, and was then handed a working password for that same address.
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const {
    data: users,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tenantUsers'],
    queryFn: () => adminService.getTenantUsers(),
  });

  /**
   * Which tenants each user may read.
   *
   * Until now nothing in the product could answer that question, and nothing
   * enforced it: cost-api took the tenant from a request header and served
   * whatever was asked for, so any signed-in user could read any tenant's
   * costs. The server now checks, and this is where the answer is set.
   */
  const {
    data: tenantAccess,
    refetch: refetchAccess,
  } = useQuery({
    queryKey: ['tenantAccess'],
    queryFn: () => adminService.getTenantAccess(),
  });

  const [savingAccessFor, setSavingAccessFor] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  const toggleTenant = async (userId: string, tenantId: string, currentIds: string[]) => {
    const next = currentIds.includes(tenantId)
      ? currentIds.filter((t) => t !== tenantId)
      : [...currentIds, tenantId];
    setSavingAccessFor(userId);
    try {
      await adminService.setTenantAccess(userId, next);
      await refetchAccess();
    } catch (e: any) {
      alert(`Could not update tenant access: ${e.message}`);
    } finally {
      setSavingAccessFor(null);
    }
  };

  /**
   * Grants every user every tenant, for users who have no entitlements yet.
   *
   * This reproduces today's effective behaviour as explicit data, which is
   * what makes turning enforcement on safe: nothing changes for anyone at the
   * moment of the flip, and access is then removed deliberately rather than
   * granted back under time pressure while people are locked out.
   */
  const handleSeed = async () => {
    if (!confirm(
      'Grant every user access to every tenant?\n\n' +
      'This only fills in users who have no access rows yet — it will not undo ' +
      'anything you have already changed. Do this once, before turning on ' +
      'enforcement, so nobody is locked out.'
    )) return;
    setSeeding(true);
    try {
      const result = await adminService.seedTenantAccess();
      alert(`Provisioned ${result.seeded} user(s). ${result.skippedAlreadyProvisioned} already had access set.`);
      await refetchAccess();
    } catch (e: any) {
      alert(`Seeding failed: ${e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  const roleLabels: Record<UserRole, string> = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.ANALYST]: 'FinOps Analyst',
    [UserRole.VIEWER]: 'Viewer',
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-200',
      inactive: 'bg-secondary text-foreground dark:bg-secondary dark:text-muted-foreground',
      pending: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900 dark:text-amber-200',
    };

    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${styles[status as keyof typeof styles]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !selectedRole) {
      alert('Please provide email and select a role');
      return;
    }
    // Belt-and-braces with the button's own disabled state: a fast double
    // click can land two onClick calls before React re-renders the button.
    if (inviteSubmitting) return;

    setInviteSubmitting(true);
    try {
      const result = await adminService.inviteUser(inviteEmail, [selectedRole]);
      const invitedEmail = inviteEmail;
      setShowInviteModal(false);
      setInviteEmail('');
      setSelectedRole('');
      await refetch();
      // Same copyable modal the password reset uses. This was an alert() of
      // the whole prose message, so the one-time password could be read but
      // not copied - and SMTP is unavailable on this deployment, meaning
      // hand-copying it is the ONLY way the new user ever receives it.
      if (result.tempPassword) {
        setResetResult({
          userName: invitedEmail,
          password: result.tempPassword,
          kind: 'invite',
          emailSent: result.emailSent,
        });
      } else {
        // Only reachable if the backend omitted the password entirely.
        alert(result.inviteMessage || 'User invited.');
      }
    } catch (error: any) {
      const backendMessage = error.response?.data?.error;
      alert(backendMessage || 'Failed to invite user');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleChangeRole = async (userId: string, userName: string, newRole: UserRole) => {
    if (!confirm(`Change ${userName}'s role to ${roleLabels[newRole]}?`)) {
      return;
    }
    try {
      await adminService.updateUserRoles(userId, [newRole]);
      await refetch();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to change role');
    }
  };

  const handleResetPassword = async (userId: string, userName: string) => {
    if (!confirm(`Reset ${userName}'s password? A new temporary password will be generated.`)) {
      return;
    }
    try {
      const tempPassword = `Reset-${Math.random().toString(36).slice(2, 10)}!`;
      await adminService.resetUserPassword(userId, tempPassword);
      // Shown in a real modal rather than alert(). A native alert() renders
      // the password as non-selectable text in most browsers, so the admin
      // could read the one-time password but not copy it - and it is never
      // displayed again, so a mistyped character means resetting all over.
      setResetResult({ userName, password: tempPassword, kind: 'reset' });
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleRemoveUser = async (userId: string, userName: string) => {
    if (!confirm(`Disable ${userName}'s account? They won't be able to log in, but this can be undone later.`)) {
      return;
    }
    try {
      await adminService.removeUser(userId);
      await refetch();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to disable user');
    }
  };

  const handleEnableUser = async (userId: string, userName: string) => {
    if (!confirm(`Re-enable ${userName}'s account? They'll be able to log in again.`)) {
      return;
    }
    try {
      await adminService.enableUser(userId);
      await refetch();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to enable user');
    }
  };

  const handleDeletePermanently = async (userId: string, userName: string) => {
    if (!confirm(`Permanently delete ${userName}'s account? This cannot be undone, and frees up their email for a fresh invite.`)) {
      return;
    }
    try {
      await adminService.deleteUserPermanently(userId);
      await refetch();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to permanently delete user');
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading users..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load users"
        message="Unable to fetch user data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">User Management</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage user access and role assignments
          </p>
        </div>
        <button
          onClick={handleSeed}
          disabled={seeding}
          title="Give every user access to every tenant, for users who have none set yet. Run this once before enforcement is switched on."
          className="mr-2 inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        >
          {seeding ? 'Provisioning…' : 'Provision tenant access'}
        </button>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
          Invite User
        </button>
      </div>

      {/* Users Table */}
      {/* overflow-x-auto, not overflow-hidden: with `min-w-full` the table
          shrinks its columns to fit the container, which squeezed the Role
          select and the action links into an unreadable stack once the
          sign-in-blocked detail and longer emails were added. Allowing
          horizontal scroll lets the columns keep their natural width. */}
      {/* Whether the tenant boundary is actually being enforced yet. Without
          this an admin ticks boxes, sees them save, and has no way to know the
          server is still letting everyone through - which is exactly the state
          this feature ships in on purpose. */}
      {tenantAccess && !tenantAccess.strict && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <span className="font-semibold">Tenant access is recorded but not yet enforced.</span>{' '}
          Cross-tenant attempts are logged only. Provision every user above, confirm the
          list looks right, then set <code className="font-mono">TENANT_AUTHZ_STRICT=true</code> on
          cost-api and optimization-api to start denying.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm dark:border-border dark:bg-card">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                User
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Roles
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Tenant Access
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Last Login
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-card dark:divide-border dark:bg-card">
            {users?.map((user: any) => (
              <tr key={user.id} className="hover:bg-muted/30 dark:hover:bg-secondary">
                <td className="whitespace-nowrap px-4 py-2.5">
                  <div className="flex items-center">
                    <div className="flex h-9 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <span className="text-sm font-medium text-primary dark:text-primary">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-foreground">
                        {user.name}
                      </div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  </div>
                </td>
                {/* whitespace-nowrap + a min-width on the select: with neither,
                    the table handed this column whatever the User and Actions
                    columns left over and the select collapsed to about the
                    width of its own chevron - the role was unreadable, so the
                    one control that says what a user can DO showed nothing.
                    8.5rem clears the longest label, "FinOps Analyst". */}
                <td className="whitespace-nowrap px-4 py-2.5">
                  <select
                    value={user.roles[0] || ''}
                    onChange={(e) => handleChangeRole(user.id, user.name, e.target.value as UserRole)}
                    className="min-w-[8.5rem] rounded-md border border-border bg-primary/10 px-2 py-1 text-xs font-medium text-primary focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-primary/10 dark:text-primary"
                  >
                    {Object.entries(roleLabels).map(([role, label]) => (
                      <option key={role} value={role}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                {/* Tenant entitlements. An empty set means "not provisioned",
                    which today still lets the user in (permissive mode) but
                    will deny them once enforcement is on - so it is called out
                    rather than rendered as an innocuous row of blanks. */}
                <td className="whitespace-nowrap px-4 py-2.5">
                  {(() => {
                    const all = tenantAccess?.tenants || [];
                    const mine = tenantAccess?.access?.[user.id] || [];
                    if (all.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
                    return (
                      <div className="flex flex-col gap-1">
                        {all.map((t) => (
                          <label
                            key={t.id}
                            className="flex cursor-pointer items-center gap-1.5 text-xs text-foreground"
                            title={t.label}
                          >
                            <input
                              type="checkbox"
                              checked={mine.includes(t.id)}
                              disabled={savingAccessFor === user.id}
                              onChange={() => toggleTenant(user.id, t.id, mine)}
                              className="h-3 w-3 rounded border-border"
                            />
                            <span className="truncate">{t.label}</span>
                          </label>
                        ))}
                        {mine.length === 0 && (
                          <span className="text-2xs font-medium text-amber-600 dark:text-amber-400">
                            Not provisioned
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  {getStatusBadge(user.status)}
                  {/* An account can be 'active' and still be unable to sign in:
                      a brute-force lockout (not cleared by re-enabling) or a
                      pending Keycloak required action (which the ROPC login
                      flow cannot complete) both block it. Keycloak returns the
                      same generic error as a wrong password for these, so
                      without this the admin has no way to tell "typed it
                      wrong" from "this account cannot log in at all".
                      Reset Password clears both. */}
                  {user.loginBlocked && user.status === 'active' && (
                    <div
                      className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400"
                      title={
                        user.bruteForceLocked
                          ? 'Locked by Keycloak brute-force detection. Use Reset Password to clear it.'
                          : `Pending action(s): ${(user.requiredActions || []).join(', ')}. Sign-in is blocked until cleared — use Reset Password.`
                      }
                    >
                      ⚠ Cannot sign in
                      {user.bruteForceLocked
                        ? ' — locked out'
                        : (user.requiredActions || []).length > 0
                          ? ` — ${(user.requiredActions || []).join(', ')}`
                          : ''}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-muted-foreground">
                  {/* "Never" was a false claim, not a missing value. The
                      backend reads this from Keycloak's `attributes.lastLogin`,
                      which Keycloak does not populate on the users endpoint -
                      so every row said "Never", including the account of the
                      admin reading the page. Real last-login needs Keycloak
                      admin events or a session lookup; until that is wired,
                      say we do not track it rather than assert something
                      untrue. */}
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString()
                    : <span title="Keycloak does not expose last-login on this endpoint; not yet wired to admin events">Not tracked</span>}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium">
                  <button
                    onClick={() => handleResetPassword(user.id, user.name)}
                    className="text-primary hover:text-primary dark:text-primary dark:hover:text-primary"
                  >
                    Reset Password
                  </button>
                  {user.status === 'active' ? (
                    <button
                      onClick={() => handleRemoveUser(user.id, user.name)}
                      className="ml-4 text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300"
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEnableUser(user.id, user.name)}
                      className="ml-4 text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      Enable
                    </button>
                  )}
                  <button
                    onClick={() => handleDeletePermanently(user.id, user.name)}
                    className="ml-4 text-rose-600 hover:text-rose-900 dark:text-rose-400 dark:hover:text-rose-300"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Password-reset result. Replaces a native alert(), whose text cannot
          be selected in most browsers - the admin could see the one-time
          password but not copy it, and it is never shown again. */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <h3 className="text-sm font-semibold text-foreground">
              {resetResult.kind === 'invite' ? 'User invited' : 'Password reset'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Share this temporary password with {resetResult.userName}. It will not be shown again.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                readOnly
                value={resetResult.password}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm text-foreground"
                aria-label="Temporary password"
              />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(resetResult.password);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch {
                    // Clipboard API needs a secure context and can be blocked
                    // by permissions policy - the input above stays
                    // selectable so manual copy still works.
                    setCopied(false);
                  }
                }}
                className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {resetResult.kind === 'invite'
                ? resetResult.emailSent
                  ? 'A welcome email has also been sent to this address.'
                  : 'No welcome email was sent (SMTP is not configured on this deployment) - this password must be shared manually.'
                : 'This also cleared any other sign-in blocks on the account (stale Keycloak actions and brute-force lockout).'}
            </p>
            {/* Both paths now issue a SINGLE-USE password: Keycloak is told the
                credential is temporary, so it prompts for a new one on first
                sign-in. Saying so prevents the obvious support ticket - an
                admin who does not expect the prompt reads it as the password
                having failed. */}
            <p className="mt-2 rounded-md bg-primary/10 px-3 py-2 text-xs text-primary">
              This is a one-time password. {resetResult.userName} will be asked to choose their own
              password the first time they sign in.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => { setResetResult(null); setCopied(false); refetch(); }}
                className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/50"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">Invite User</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@company.com"
                  className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground/80">
                  Role
                </label>
                <div className="mt-2 space-y-2">
                  {Object.entries(roleLabels).map(([role, label]) => (
                    <label key={role} className="flex items-center">
                      <input
                        type="radio"
                        name="invite-role"
                        checked={selectedRole === role}
                        onChange={() => setSelectedRole(role as UserRole)}
                        className="h-4 w-4 border-border text-primary focus:ring-ring dark:border-border dark:bg-secondary"
                      />
                      <span className="ml-2 text-sm text-foreground/80">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-border pt-4 dark:border-border">
              <button
                onClick={() => setShowInviteModal(false)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteUser}
                disabled={inviteSubmitting}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
              >
                {inviteSubmitting ? 'Sending…' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
